import { useState } from 'react'

import { type LibraryDocument, previewUrl } from '../../api/library'
import { type Note, firstLineOf } from '../../api/notes'
import {
  type BoardHit,
  isFlashcard,
  isAppointment,
  isNote,
  isSubject,
  isTask,
  partsOf,
  type SearchHit,
} from '../../api/search'
import type { Flashcard } from '../../api/flashcards'
import { type Subject, totalOf } from '../../api/subjects'
import { type Appointment, whatItIsAbout } from '../../api/timetable'
import { type Task, timeLeft } from '../../api/tasks'
import { asDate } from '../library/Tiles'

/**
 * One hit: the document as its content, the passage the words were found in, and where it lives.
 *
 * The same rule as in the library — **a document appears as what it is**, not as an icon beside a file
 * name — and one more that only a search needs: the passage. A result list without it is a list of file
 * names to open one after another, which is exactly the search MyLib is meant not to be. With it,
 * somebody can see from the list whether this is the thing they half remember.
 *
 * A row rather than a tile, and that is the only place the search departs from the shelf: a passage of
 * two lines does not fit under a picture in a narrow grid, and it is the more important half.
 *
 * **A note is drawn as a note.** It has no picture, no folder and no kind of file, and pretending
 * otherwise — a grey placeholder where a first page would be — would be the file list this product is
 * meant not to feel like. What it has is its first line, drawn as it was written, which is exactly what
 * the pad shows of it.
 *
 * **A task can be ticked off from here**, and it is the one thing in a result list that *does*
 * something rather than leading somewhere. The reason is what a search over open points is for: the
 * answer to "have I still got to do that?" is very often "no — and now it is done". Making somebody
 * open the list to say so would be a step for its own sake.
 *
 * The box is a sibling of the control that opens the hit, not a child of it: a checkbox inside a
 * button is not a thing, and one nested in another control is a control neither the keyboard nor a
 * screen reader can reach cleanly. Beside it, while the keyboard is on this row, stands the shortcut
 * that does the same — because a key nobody can see is a key nobody uses.
 */
export interface HitProps {
  readonly hit: SearchHit
  /** Where this document lives, spelled out: `Semester 3 / Klausuren`. Empty at the top of the library. */
  readonly folder: string
  /** Whether the keyboard is currently on this hit. */
  readonly active: boolean
  readonly id: string
  readonly onOpen: () => void
  /** Ticks a task off, or takes that back. Absent where the hit is not a task. */
  readonly onTickOff?: (done: boolean) => void
}

export function Hit({ hit, folder, active, id, onOpen, onTickOff }: HitProps) {
  const task = isTask(hit) ? (hit.task ?? null) : null
  const tickable = task !== null && onTickOff !== undefined

  return (
    <li
      className={tickable ? 'hit hit--tickable' : 'hit'}
      role="option"
      id={id}
      aria-selected={active}
    >
      <button type="button" className="hit__open" data-active={active} onClick={onOpen}>
        {isNote(hit) && hit.note !== null && hit.note !== undefined ? (
          <AsNote note={hit.note} snippet={hit.snippet} />
        ) : task !== null ? (
          <AsTask task={task} snippet={hit.snippet} />
        ) : isFlashcard(hit) && hit.flashcard !== null && hit.flashcard !== undefined ? (
          <AsFlashcard card={hit.flashcard} snippet={hit.snippet} />
        ) : hit.boardElement !== null && hit.boardElement !== undefined ? (
          <AsBoardElement on={hit.boardElement} snippet={hit.snippet} />
        ) : isSubject(hit) && hit.subject !== null && hit.subject !== undefined ? (
          <AsSubject subject={hit.subject} snippet={hit.snippet} />
        ) : isAppointment(hit) && hit.appointment !== null && hit.appointment !== undefined ? (
          <AsAppointment
            appointment={hit.appointment}
            snippet={hit.snippet}
            /*
              A write-up is drawn as the session it is about, and says so. Its id in the index is the
              appointment's own, so this is the same row with one word different — and that word is
              the one that tells "the lecture said this" from "I wrote this".
            */
            written={hit.kind === 'writeup'}
          />
        ) : (
          <AsDocument document={hit.document ?? null} snippet={hit.snippet} folder={folder} />
        )}
      </button>
      {tickable && (
        <span className="hit__doing">
          {active && (
            /* What the keyboard would do here, shown only while the keyboard is here. */
            <span className="hit__shortcut" aria-hidden="true">
              ⇧↵
            </span>
          )}
          <input
            type="checkbox"
            className="hit__tick"
            checked={task.doneAt !== null}
            aria-label={task.doneAt !== null ? `Take back: ${task.text}` : `Tick off: ${task.text}`}
            onChange={(event) => {
              onTickOff(event.target.checked)
            }}
          />
        </span>
      )}
    </li>
  )
}

/**
 * A subject: the bracket, not what is under it.
 *
 * It says how much it holds, because that is what somebody is deciding by when a subject turns up
 * among their hits — "is this the one with all the scripts in it".
 */
function AsSubject({ subject, snippet }: { readonly subject: Subject; readonly snippet: string }) {
  return (
    <>
      <span className="hit__picture hit__picture--note">
        <span className="hit__instead" aria-hidden="true">
          SUBJ
        </span>
      </span>
      <span className="hit__what">
        <span className="hit__title">{subject.name}</span>
        <Passage snippet={snippet} />
        <span className="hit__facts">
          <span className="hit__kind">Subject</span>
          <span>{`${String(totalOf(subject.counts))} things`}</span>
          {subject.archivedAt !== null && <span>Archived</span>}
        </span>
      </span>
    </>
  )
}

/** One entry in the timetable, drawn as when it is and where. */
function AsAppointment({
  appointment,
  snippet,
  written = false,
}: {
  readonly appointment: Appointment
  readonly snippet: string
  /** Whether the passage is what somebody wrote about the session rather than the session itself. */
  readonly written?: boolean
}) {
  const at = new Date(appointment.startsAt)
  const about = whatItIsAbout(appointment)
  return (
    <>
      <span className="hit__picture hit__picture--note">
        <span className="hit__instead" aria-hidden="true">
          {written ? 'WROTE' : 'PLAN'}
        </span>
      </span>
      <span className="hit__what">
        <span className="hit__title">{appointment.title}</span>
        <Passage snippet={snippet} />
        <span className="hit__facts">
          <span className="hit__kind">{written ? 'What you wrote' : 'Timetable'}</span>
          {!Number.isNaN(at.getTime()) && <span>{at.toLocaleString()}</span>}
          {appointment.room !== '' && <span>{appointment.room}</span>}
          {about !== '' && about !== appointment.title && <span>{about}</span>}
        </span>
      </span>
    </>
  )
}

function AsFlashcard({ card, snippet }: { readonly card: Flashcard; readonly snippet: string }) {
  const title = firstLineOfText(card.front)
  return (
    <>
      <span className="hit__picture hit__picture--note">
        <span className="hit__instead" aria-hidden="true">
          CARD
        </span>
      </span>
      <span className="hit__what">
        <span className="hit__title">{title === '' ? 'Flashcard' : title}</span>
        <Passage snippet={snippet} />
        <span className="hit__facts">
          <span className="hit__kind">Flashcard</span>
          {card.tags.map((tag) => (
            <span key={tag.id}>{tag.name}</span>
          ))}
        </span>
      </span>
    </>
  )
}

function AsDocument({
  document,
  snippet,
  folder,
}: {
  readonly document: LibraryDocument | null
  readonly snippet: string
  readonly folder: string
}) {
  if (document === null) {
    return null
  }
  return (
    <>
      <span className="hit__picture">
        {/* Keyed by the content, so an edited document asks for its picture again. See `Tiles`. */}
        <Picture key={document.contentId} document={document} />
      </span>
      <span className="hit__what">
        <span className="hit__title">{document.title}</span>
        <Passage snippet={snippet} />
        <span className="hit__facts">
          {folder !== '' && <span>{folder}</span>}
          <span className="hit__kind">{document.kind === '' ? 'File' : document.kind}</span>
          <span>{asDate(document.takenOverAt)}</span>
          {came(document) !== null && <span>{came(document)}</span>}
        </span>
      </span>
    </>
  )
}

function AsNote({ note, snippet }: { readonly note: Note; readonly snippet: string }) {
  const line = firstLineOf(note)
  return (
    <>
      <span className="hit__picture hit__picture--note">
        <span className="hit__instead" aria-hidden="true">
          NOTE
        </span>
      </span>
      <span className="hit__what">
        <span className="hit__title">{line === '' ? 'Empty note' : line}</span>
        <Passage snippet={snippet} />
        <span className="hit__facts">
          <span className="hit__kind">Note</span>
          <span>{asDate(note.createdAt)}</span>
        </span>
      </span>
    </>
  )
}

/**
 * A task is drawn as a task.
 *
 * Its sentence is its title, because that is all it has and all it needs; beside it stands what the
 * list would say about it — ticked off, or how much time is left. A hit that only said "Task" and a
 * date would make somebody open it to find out whether it is the one they meant.
 */
function AsTask({ task, snippet }: { readonly task: Task; readonly snippet: string }) {
  return (
    <>
      <span className="hit__picture hit__picture--task">
        <span className="hit__instead" aria-hidden="true">
          TASK
        </span>
      </span>
      <span className="hit__what">
        <span className="hit__title">{task.text}</span>
        <Passage snippet={snippet} />
        <span className="hit__facts">
          <span className="hit__kind">Task</span>
          {task.doneAt !== null && <span>Ticked off</span>}
          {task.doneAt === null && task.dueOn !== null && <span>{timeLeft(task)}</span>}
        </span>
      </span>
    </>
  )
}

/**
 * Something written on a board is drawn as what it is: its own first line, and the board it lies on.
 *
 * Naming the board is the whole difference from a note. A sticky note has no folder and no place in
 * a list — where it *is* is a spot on a surface, and "on Semester 3" is what tells somebody whether
 * this is the thought they half remember. Taking the hit leads to that spot, not merely to the board.
 */
function AsBoardElement({ on, snippet }: { readonly on: BoardHit; readonly snippet: string }) {
  const line = firstLineOfText(on.element.text ?? '')
  return (
    <>
      <span className="hit__picture hit__picture--note">
        <span className="hit__instead" aria-hidden="true">
          BOARD
        </span>
      </span>
      <span className="hit__what">
        <span className="hit__title">{line === '' ? 'On a board' : line}</span>
        <Passage snippet={snippet} />
        <span className="hit__facts">
          <span className="hit__kind">
            {on.element.kind === 'text' ? 'Text block' : 'Sticky note'}
          </span>
          <span>{on.boardName}</span>
          <span>{asDate(on.element.createdAt)}</span>
        </span>
      </span>
    </>
  )
}

/**
 * The passage, with the found words marked.
 *
 * Cut at two control characters into React elements — never `dangerouslySetInnerHTML`. The text comes
 * out of somebody's PDF, and there is no version of trusting that which stays safe.
 */
function Passage({ snippet }: { readonly snippet: string }) {
  const parts = partsOf(snippet)
  if (parts.length === 0) {
    return null
  }
  return (
    <span className="hit__passage">
      {/* The parts of one passage have no identity but their order, so that is what keys them. */}
      {parts.map((part, index) =>
        part.found ? (
          <mark key={`${index}-${part.text}`}>{part.text}</mark>
        ) : (
          <span key={`${index}-${part.text}`}>{part.text}</span>
        ),
      )}
    </span>
  )
}

/** The first line of what is written on a board — the same thing the pad shows of a note. */
function firstLineOfText(text: string): string {
  return (
    text
      .split('\n')
      .map((one) => one.trim())
      .find((one) => one !== '') ?? ''
  )
}

/** Where this document came from, where that is worth a word. */
function came(document: LibraryDocument): string | null {
  const gone = document.origins.find((origin) => origin.goneFromSourceAt !== null)
  if (gone !== undefined) {
    return gone.source === '' ? 'No longer in its source' : `No longer in ${gone.source}`
  }
  const system = document.origins.find((origin) => origin.source !== '')
  return system === undefined ? null : `From ${system.source}`
}

/** The picture of the content, or what the file is in type. Exactly as a tile does it. */
function Picture({ document }: { readonly document: LibraryDocument }) {
  const [drawn, setDrawn] = useState(document.hasPreview)

  if (!drawn) {
    return (
      <span className="hit__instead" aria-hidden="true">
        {document.kind === '' ? 'FILE' : document.kind}
      </span>
    )
  }

  return (
    <img
      className="hit__image"
      src={previewUrl(document)}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => {
        setDrawn(false)
      }}
    />
  )
}
